/**
 * 版本历史数据转换（Wave 4 R3 / T4.7 集成）。
 *
 * 将后端 /debugpavilion/schemas/{id}/versions 的原始记录转换为 UI 模型，
 * 并为 change_type 提供人读标签。纯函数，便于单元测试。
 */
import type { SchemaVersion } from "@/services/debugpavilion";

/** 版本历史 UI 行模型。 */
export interface VersionHistoryItem {
  /** 版本记录 id */
  id: string;
  /** 版本号（>= 1，v1 为初始创建） */
  version: number;
  /** 变更类型（create/update/restore/...），无则 null */
  changeType: string | null;
  /** 变更类型人读标签 */
  changeTypeLabel: string;
  /** 变更说明 */
  changeLog: string | null;
  /** 创建时间（ISO 字符串） */
  createdAt: string;
}

const CHANGE_TYPE_LABELS: Record<string, string> = {
  create: "创建",
  update: "更新",
  restore: "恢复",
  publish: "发布",
};

/** 变更类型 → 中文标签；未知类型回退为原值。 */
export function changeTypeLabel(changeType: string | null | undefined): string {
  if (!changeType) return "版本";
  return CHANGE_TYPE_LABELS[changeType] ?? changeType;
}

/** 后端版本记录列表 → UI 行模型（保持后端返回顺序：新 → 旧）。 */
export function toVersionHistoryItems(versions: SchemaVersion[]): VersionHistoryItem[] {
  return versions.map((v) => ({
    id: v.id,
    version: v.version,
    changeType: v.change_type,
    changeTypeLabel: changeTypeLabel(v.change_type),
    changeLog: v.change_log,
    createdAt: v.created_at,
  }));
}

/** 格式化 ISO 时间为本地可读字符串；非法输入返回 "—"。 */
export function formatVersionTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
